import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container, Typography, Box, Card, CardContent, Grid,
  Chip, Button, CircularProgress, Avatar, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
} from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AirlineSeatReclineNormalIcon from '@mui/icons-material/AirlineSeatReclineNormal';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import Rating from '@mui/material/Rating';
import carpoolService from '../../services/carpool.service';
import { TZ } from '../../utils/date';

const STATUS_CONFIG = {
  pending:   { label: 'En attente',  color: '#f39c12', bg: 'rgba(243,156,18,0.12)',  border: 'rgba(243,156,18,0.3)',  icon: <HourglassEmptyIcon sx={{ fontSize: 14 }} /> },
  confirmed: { label: 'Confirmée',   color: '#2ecc71', bg: 'rgba(46,204,113,0.12)',  border: 'rgba(46,204,113,0.3)',  icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
  completed: { label: 'Terminée',    color: '#F0EBE0', bg: 'rgba(240,235,224,0.08)', border: 'rgba(240,235,224,0.2)', icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
  cancelled: { label: 'Annulée',     color: '#e74c3c', bg: 'rgba(231,76,60,0.12)',   border: 'rgba(231,76,60,0.3)',  icon: <CancelIcon sx={{ fontSize: 14 }} /> },
};

const CarpoolBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelDialog, setCancelDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    carpoolService.getMyBookings()
      .then(data => setBookings(data.bookings || []))
      .catch(() => setError('Impossible de charger vos réservations'))
      .finally(() => setLoading(false));
  }, []);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await carpoolService.cancelBooking(selectedId);
      setBookings(prev => prev.map(b =>
        b.id === selectedId ? { ...b, booking_status: 'cancelled' } : b
      ));
    } catch {
      setError('Impossible d\'annuler cette réservation');
    } finally {
      setCancelling(false);
      setCancelDialog(false);
      setSelectedId(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: '#F0EBE0' }} />
      </Box>
    );
  }

  const active    = bookings.filter(b => ['pending', 'confirmed'].includes(b.booking_status));
  const past      = bookings.filter(b => ['completed', 'cancelled'].includes(b.booking_status));

  const BookingCard = ({ booking }) => {
    const status = STATUS_CONFIG[booking.booking_status] || STATUS_CONFIG.pending;
    const canCancel = booking.booking_status === 'pending';

    return (
      <Card
        sx={{
          transition: 'all 0.3s',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 12px 40px rgba(240,235,224,0.1)',
            border: '1px solid rgba(240,235,224,0.2)',
          },
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Grid container spacing={2} alignItems="center">

            {/* Conducteur */}
            <Grid item xs={12} sm={2.5}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <Avatar
                  sx={{
                    width: 46, height: 46,
                    bgcolor: 'rgba(240,235,224,0.12)',
                    color: '#F0EBE0',
                    border: '2px solid rgba(240,235,224,0.25)',
                    fontWeight: 700,
                  }}
                >
                  {booking.driver_first_name?.charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={700} sx={{ color: 'white' }}>
                    {booking.driver_first_name} {booking.driver_last_name}
                  </Typography>
                  {booking.driver_rating > 0 && (
                    <Rating
                      value={booking.driver_rating}
                      size="small"
                      readOnly
                      precision={0.1}
                      sx={{ '& .MuiRating-iconFilled': { color: '#ffc107' }, fontSize: '0.75rem' }}
                    />
                  )}
                </Box>
              </Box>
            </Grid>

            {/* Itinéraire */}
            <Grid item xs={12} sm={5.5}>
              <Box display="flex" alignItems="center" gap={2}>
                <Box sx={{ flex: 1 }}>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.3}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#F0EBE0', flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem' }}>
                      Départ
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600} sx={{ color: 'white' }} noWrap>
                    {booking.departure_address}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5} mt={0.3}>
                    <AccessTimeIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,0.35)' }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>
                      {booking.departure_time}
                    </Typography>
                  </Box>
                </Box>

                <ArrowForwardIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.25)', flexShrink: 0 }} />

                <Box sx={{ flex: 1 }}>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.3}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#e74c3c', flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem' }}>
                      Arrivée
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight={600} sx={{ color: 'white' }} noWrap>
                    {booking.arrival_address}
                  </Typography>
                </Box>
              </Box>

              {/* Date + places */}
              <Box display="flex" gap={1} mt={1.5} flexWrap="wrap">
                <Chip
                  icon={<CalendarTodayIcon sx={{ fontSize: 13 }} />}
                  label={new Date(booking.booking_date).toLocaleDateString('fr-FR', { timeZone: TZ, weekday: 'short', day: 'numeric', month: 'short' })}
                  size="small"
                  sx={{ height: 24, fontSize: '0.7rem', bgcolor: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.06)', '& .MuiChip-icon': { color: 'rgba(255,255,255,0.35)' } }}
                />
                <Chip
                  icon={<AirlineSeatReclineNormalIcon sx={{ fontSize: 13 }} />}
                  label={`${booking.seats_booked} place${booking.seats_booked > 1 ? 's' : ''}`}
                  size="small"
                  sx={{ height: 24, fontSize: '0.7rem', bgcolor: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.06)', '& .MuiChip-icon': { color: 'rgba(255,255,255,0.35)' } }}
                />
              </Box>
            </Grid>

            {/* Prix + statut + action */}
            <Grid item xs={12} sm={4}>
              <Box display="flex" alignItems={{ xs: 'center', sm: 'flex-end' }} flexDirection={{ xs: 'row', sm: 'column' }} justifyContent={{ xs: 'space-between', sm: 'flex-end' }} gap={1.5} sx={{ textAlign: { sm: 'right' } }}>
                <Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #2D4A35, #1E3525)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      lineHeight: 1,
                    }}
                  >
                    {booking.total_price} €
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                    total
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1} flexDirection={{ xs: 'row', sm: 'row' }}>
                  <Chip
                    icon={React.cloneElement(status.icon, { sx: { fontSize: 14, color: `${status.color} !important` } })}
                    label={status.label}
                    size="small"
                    sx={{
                      height: 26,
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      bgcolor: status.bg,
                      color: status.color,
                      border: `1px solid ${status.border}`,
                      '& .MuiChip-icon': { color: status.color },
                    }}
                  />
                  {canCancel && (
                    <Button
                      size="small"
                      onClick={() => { setSelectedId(booking.id); setCancelDialog(true); }}
                      sx={{
                        color: 'rgba(231,76,60,0.7)',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        borderRadius: '8px',
                        border: '1px solid rgba(231,76,60,0.2)',
                        px: 1.5,
                        '&:hover': { bgcolor: 'rgba(231,76,60,0.08)', color: '#e74c3c' },
                      }}
                    >
                      Annuler
                    </Button>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', pb: 8, pt: 4 }}>
      <Container maxWidth="lg">

        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 5 }}>
          <Box>
            <Chip
              icon={<GroupIcon sx={{ color: '#F0EBE0 !important', fontSize: 15 }} />}
              label="MES TRAJETS"
              sx={{ mb: 1.5, bgcolor: 'rgba(240,235,224,0.1)', color: '#F0EBE0', border: '1px solid rgba(240,235,224,0.2)', fontWeight: 700, fontSize: '0.68rem', letterSpacing: '0.08em' }}
            />
            <Typography variant="h4" fontWeight={900} sx={{ color: 'white', lineHeight: 1.1 }}>
              Mes réservations
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.5 }}>
              {bookings.length} réservation{bookings.length > 1 ? 's' : ''} au total
            </Typography>
          </Box>
          <Box display="flex" gap={1.5}>
            <Button
              component={Link}
              to="/carpool"
              variant="outlined"
              sx={{ borderColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.6)', borderRadius: '12px', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: 'rgba(240,235,224,0.3)', bgcolor: 'rgba(240,235,224,0.05)' } }}
            >
              Rechercher
            </Button>
            <Button
              component={Link}
              to="/carpool/create"
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ background: 'linear-gradient(135deg, #2D4A35, #1E3525)', borderRadius: '12px', textTransform: 'none', fontWeight: 700, boxShadow: '0 4px 16px rgba(240,235,224,0.2)', '&:hover': { background: 'linear-gradient(135deg, #1E3525, #162B1D)', transform: 'translateY(-1px)' } }}
            >
              Proposer
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {bookings.length === 0 ? (
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 10 }}>
              <DirectionsCarIcon sx={{ fontSize: 72, color: 'rgba(255,255,255,0.1)', mb: 2 }} />
              <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1 }}>
                Aucune réservation pour l'instant
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.3)', mb: 4 }}>
                Trouvez un trajet qui vous correspond
              </Typography>
              <Button
                component={Link}
                to="/carpool"
                variant="contained"
                size="large"
                startIcon={<GroupIcon />}
                sx={{ background: 'linear-gradient(135deg, #2D4A35, #1E3525)', borderRadius: '14px', textTransform: 'none', fontWeight: 700, px: 4, boxShadow: '0 8px 30px rgba(240,235,224,0.25)' }}
              >
                Rechercher un trajet
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Reservations actives */}
            {active.length > 0 && (
              <Box sx={{ mb: 5 }}>
                <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: '0.1em', fontSize: '0.7rem', display: 'block', mb: 2 }}>
                  À venir · {active.length}
                </Typography>
                <Grid container spacing={2}>
                  {active.map(booking => (
                    <Grid item xs={12} key={booking.id}>
                      <BookingCard booking={booking} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Separateur */}
            {active.length > 0 && past.length > 0 && (
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />
            )}

            {/* Historique */}
            {past.length > 0 && (
              <Box>
                <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: '0.1em', fontSize: '0.7rem', display: 'block', mb: 2 }}>
                  Historique · {past.length}
                </Typography>
                <Grid container spacing={2}>
                  {past.map(booking => (
                    <Grid item xs={12} key={booking.id}>
                      <Box sx={{ opacity: booking.booking_status === 'cancelled' ? 0.55 : 0.8 }}>
                        <BookingCard booking={booking} />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </>
        )}
      </Container>

      {/* Dialog confirmation annulation */}
      <Dialog
        open={cancelDialog}
        onClose={() => setCancelDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(15,15,15,0.97)',
            backdropFilter: 'blur(40px)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '20px',
          },
        }}
      >
        <DialogTitle sx={{ color: 'white', fontWeight: 700 }}>
          Annuler la réservation ?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Cette action est irréversible. Votre place sera libérée pour d'autres passagers.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, gap: 1.5 }}>
          <Button
            onClick={() => setCancelDialog(false)}
            disabled={cancelling}
            sx={{ color: 'rgba(255,255,255,0.6)', textTransform: 'none', fontWeight: 600, borderRadius: '10px' }}
          >
            Garder
          </Button>
          <Button
            onClick={handleCancel}
            disabled={cancelling}
            variant="contained"
            startIcon={cancelling ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <CancelIcon />}
            sx={{ bgcolor: '#e74c3c', borderRadius: '10px', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#c0392b' } }}
          >
            {cancelling ? 'Annulation...' : 'Confirmer l\'annulation'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CarpoolBookings;
